import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  isLoading: false,
  error: null,
  purchaseOrders: [],
  purchaseOrder: null,
  warehouses: [],
  purchaseOrderInvoice: [],
};

const slice = createSlice({
  name: "purchaseOrder",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getPurchaseOrdersSuccess(state, action) {
      state.isLoading = false;
      state.purchaseOrders = action.payload;
    },
    getPurchaseOrderSuccess(state, action) {
      state.isLoading = false;
      state.purchaseOrder = action.payload;
    },
    getWareHouseSuccess(state, action) {
      state.isLoading = false;
      state.warehouses = action.payload;
    },
    getPurchaseOrderInvoiceSuccess(state, action) {
      state.isLoading = false;
      state.purchaseOrderInvoice = action.payload;
    },
    resetStockStore() {
      return initialState;
    },
    updateOnePurchaseOrder(state, action) {
      const updatedOrder = action.payload;
      const index = state.purchaseOrders.findIndex(
        (order) => order._id === updatedOrder._id
      );
      if (index !== -1) {
        state.purchaseOrders[index] = updatedOrder;
      }
      state.isLoading = false;
    },
  },
});

export default slice.reducer;

export function getPurchaseOrders(venueId, levelId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${backendUrl}/purchaseOrder`, {
        params: {
          venueId,
          levelId,
          isDeleted: false,
          isActivePurchaseOrder: false,
          limit: 100000000,
        },
      });

      dispatch(slice.actions.getPurchaseOrdersSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getWareHouses(franchiseId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${backendUrl}/warehouse/getWarehouseListByFranchise/${franchiseId}?isDeleted=false`
      );
      dispatch(slice.actions.getWareHouseSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPurchaseOrder(adjustmentId, venueUserId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(
        `${backendUrl}/purchaseOrder/openPurchaseOrderToEdit/${adjustmentId}?venueUserId=${venueUserId}`
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPurchaseOrderForReplicate(purchaseOrderId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(
        `${backendUrl}/purchaseOrder/replicate-purchase-order`,
        {
          purchaseOrderId,
        }
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getActiveInventoryPurchaseOrder(venueId, levelId, venueUserId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${backendUrl}/purchaseOrder/activePurchaseOrder`,
        {
          params: {
            venueId,
            levelId,
            venueUserId: venueUserId,
          },
        }
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function addIngredientInPurchaseOrder(data, ingredientId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(
        `${backendUrl}/purchaseOrder/addIngredientInPurchaseOrder/${ingredientId}`,
        data
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteIngredientFromPurchaseOrder(data, ingredientId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(
        `${backendUrl}/purchaseOrder/deleteIngredientFromPurchaseOrder/${ingredientId}`,
        data
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removeDraftedIngredientsFromPurchaseOrder(
  deletedId,
  deletedById
) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.delete(
        `${backendUrl}/purchaseOrder/${deletedId}`,
        {
          params: {
            deletedById,
            deletedBy: "venueUser",
          },
        }
      );
      dispatch(slice.actions.getPurchaseOrdersSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function receivedOrCancelDispatehedPurchaseOrder(
  id,
  purchaseOrderState,
  rejectionReason
) {
  return async (dispatch) => {
    if (purchaseOrderState !== "cancelled") {
      dispatch(slice.actions.startLoading());
    }
    try {
      const body = { purchaseOrderState };
      if (rejectionReason) body.rejectionReason = rejectionReason;
      const response = await axios.patch(
        `${backendUrl}/purchaseOrder/${id}`,
        body
      );
      dispatch(slice.actions.updateOnePurchaseOrder(response?.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// Purchase order invoice

export function getPurchaseOrdersInvoice(venueId, levelId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${backendUrl}/purchaseOrderInvoice`, {
        params: {
          venueId,
          levelId,
          isDeleted: false,
          limit: 100000000,
          excludeStatus: "drafted",
        },
      });

      dispatch(
        slice.actions.getPurchaseOrderInvoiceSuccess(response.data.results)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function PurchaseOrdersInvoicePaid(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(
        `${backendUrl}/purchaseOrderInvoice/paid/${data.invoiceId}`,
        {
          paymentType: data?.paymentType,
          paymentMethodId: data?.paymentMethodId,
          paymentMethod: data?.selectedMethod,
          paidBy: data?.paidBy,
          total: data?.total,
          currency: data?.currency,
        }
      );

      dispatch(slice.actions.getPurchaseOrderInvoiceSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function cleaPurchaseOrderAfterUpdating() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.resetStockStore());
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
